import { useCallback } from 'react'
import { useFirebaseConfigValue } from './config'
import type { AuthErrorFunction, ErrorWrapper, LoginMethod } from './types'

export const useFirebaseAuth = () => {
  const { authErrorFunction, loginMethods, translateText, showNotification } =
    useFirebaseConfigValue()

  const withErrorHandling: AuthErrorFunction = useCallback(
    async (operation) => {
      const { success, error, user } = await authErrorFunction(operation)
      const currentError = error ? (translateText?.(error) ?? error) : ''
      if (currentError) {
        showNotification?.({ message: currentError, type: 'error' })
      }
      return {
        success,
        error: currentError,
        user,
      }
    },
    [authErrorFunction, showNotification, translateText],
  )

  const loginWithEmail: ErrorWrapper<LoginMethod['loginWithEmail']> =
    useCallback(
      (props) => withErrorHandling(() => loginMethods.loginWithEmail(props)),
      [loginMethods, withErrorHandling],
    )

  const registerWithEmail: ErrorWrapper<LoginMethod['registerWithEmail']> =
    useCallback(
      (props) => withErrorHandling(() => loginMethods.registerWithEmail(props)),
      [loginMethods, withErrorHandling],
    )

  const logout: LoginMethod['logout'] = useCallback(
    () => loginMethods.logout(),
    [loginMethods],
  )

  const loginWithGoogle: ErrorWrapper<LoginMethod['loginWithGoogle']> =
    useCallback(
      (props) => withErrorHandling(() => loginMethods.loginWithGoogle(props)),
      [loginMethods, withErrorHandling],
    )

  const loginWithApple: ErrorWrapper<LoginMethod['loginWithApple']> =
    useCallback(
      (props) => withErrorHandling(() => loginMethods.loginWithApple(props)),
      [loginMethods, withErrorHandling],
    )

  return {
    loginWithEmail,
    registerWithEmail,
    logout,
    loginWithGoogle,
    loginWithApple,
  }
}
